import Switcher from "@components/switcher/Switcher";
import { KycData } from "@utils/clxm/kycdata";
import React from "react";
import SlidingPanel from "./SlidingPanel";

interface KycDumpSliderProps {
  isOpen: boolean;
  onClose: () => void;
  kycDumpData: KycData;
}

const KycDumpSlider: React.FC<KycDumpSliderProps> = ({
  isOpen,
  onClose,
  kycDumpData,
}) => {
  const hasPoiData = kycDumpData.idDump || kycDumpData.poiFrontUrl;
  const hasPoaData =
    kycDumpData.poaDump || kycDumpData.poaFrontUrl || kycDumpData.poaBackUrl;

  const getPoiData = () => {
    const frontCardData = kycDumpData.idDump?.result?.results?.find(
      (module: any) => module.module === "ID Card Validation front"
    );

    const backCardData = kycDumpData.idDump?.result?.results?.find(
      (module: any) => module.module === "ID Card Validation back"
    );

    const frontFields =
      frontCardData?.apiResponse?.result?.details?.[0]?.fieldsExtracted || {};
    const idType =
      frontCardData?.apiResponse?.result?.details?.[0]?.idType || "Unknown";

    const backFields =
      backCardData?.apiResponse?.result?.details?.[0]?.fieldsExtracted || {};

    return {
      fullName: frontFields.fullName?.value || "",
      idNumber: frontFields.idNumber?.value || "",
      gender: frontFields.gender?.value || "",
      yearOfBirth: frontFields.yearOfBirth?.value || "",
      fatherName: backFields.fatherName?.value || "",
      address: backFields.address?.value || "",
      addressStreet: backFields.address?.street || "",
      addressZipCode: backFields.address?.zipCode || "",
      addressProvince: backFields.address?.province || "",
      documentType: idType
        .replace(/_/g, " ")
        .replace(/front|back|bottom/g, "")
        .trim(),
      applicationStatus: kycDumpData.idDump?.result?.applicationStatus || "",
    };
  };

  const getPoaData = () => {
    const frontAadhaarData = kycDumpData.poaDump?.result?.results?.find(
      (module: any) => module.module === "Aadhaar OCR front"
    );

    const backAadhaarData = kycDumpData.poaDump?.result?.results?.find(
      (module: any) => module.module === "Aadhaar OCR back"
    );

    const frontFields =
      frontAadhaarData?.apiResponse?.result?.details?.[0]?.fieldsExtracted ||
      {};
    const backFields =
      backAadhaarData?.apiResponse?.result?.details?.[0]?.fieldsExtracted || {};

    const userDetails = kycDumpData.poaDump?.result?.userDetails || {};

    return {
      fullName:
        frontFields.fullName?.value ||
        userDetails["Aadhaar_OCR_Full_Name_-_Front"] ||
        "",
      idNumber:
        frontFields.idNumber?.value ||
        userDetails["Aadhaar_OCR_ID_Number_-_Front"] ||
        "",
      gender: frontFields.gender?.value || "",
      yearOfBirth: frontFields.yearOfBirth?.value || "",
      fatherName:
        frontFields.fatherName?.value || backFields.fatherName?.value || "",
      address:
        backFields.address?.value ||
        userDetails["Aadhaar_OCR_Address_-_Back"] ||
        "",
      addressStreet: backFields.address?.street || "",
      addressZipCode: backFields.address?.zipCode || "",
      addressProvince: backFields.address?.province || "",
      documentType: "Aadhaar",
      applicationStatus: kycDumpData.poaDump?.result?.applicationStatus || "",
      frontImageUrl:
        userDetails["Aadhaar_OCR_ID_Front_Image_-_Full_(S3_URL)"] || "",
      backImageUrl:
        userDetails["Aadhaar_OCR_ID_Back_Image_-_Full_(S3_URL)"] || "",
      selfieImageUrl:
        userDetails["Selfie_Validation_Selfie_Image_URL_(S3_URL)"] || "",
    };
  };

  const renderPoiContent = () => {
    const poiData = getPoiData();

    return (
      <div className="p-3 overflow-y-auto text-text-base max-h-[80vh]">
        <div className="mb-3">
          <h2 className="text-sm font-semibold mb-1">Document Information</h2>
          <div className="grid grid-cols-2 gap-2 px-2 py-1 bg-bg-base rounded border border-border-subtle">
            <div>
              <p className="text-xs font-medium text-text-subtle">
                Document Type
              </p>
              <p className="text-xs">{poiData.documentType || "ID Document"}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle">
                Application Status
              </p>
              <p
                className={`text-xs ${
                  poiData.applicationStatus === "auto_approved"
                    ? "text-text-success"
                    : "text-text-error"
                }`}
              >
                {poiData.applicationStatus.replace(/_/g, " ")}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <h2 className="text-sm font-semibold mb-1">Personal Details</h2>
          <div className="grid grid-cols-2 gap-2 px-2 py-1 bg-bg-base rounded border border-border-subtle">
            <div>
              <p className="text-xs font-medium text-text-subtle">Full Name</p>
              <p className="text-xs">{poiData.fullName}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle">ID Number</p>
              <p className="text-xs">{poiData.idNumber}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle">Gender</p>
              <p className="text-xs">
                {poiData.gender === "M"
                  ? "Male"
                  : poiData.gender === "F"
                  ? "Female"
                  : poiData.gender}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle">
                Year of Birth
              </p>
              <p className="text-xs">{poiData.yearOfBirth}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle">
                Father's Name
              </p>
              <p className="text-xs">{poiData.fatherName}</p>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <h2 className="text-sm font-semibold mb-1">Document Images</h2>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs font-medium text-text-subtle mb-1">
                Front Side
              </p>
              {kycDumpData.poiFrontUrl && (
                <div className="border border-border-subtle rounded overflow-hidden">
                  <img
                    src={kycDumpData.poiFrontUrl}
                    alt="ID Front"
                    className="w-full h-auto"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPoaContent = () => {
    const poaData = getPoaData();

    return (
      <div className="p-3 overflow-y-auto text-text-base max-h-[80vh]">
        <div className="mb-3">
          <h2 className="text-sm font-semibold mb-1">Document Information</h2>
          <div className="grid grid-cols-2 gap-2 px-2 py-1 bg-bg-base rounded border border-border-subtle">
            <div>
              <p className="text-xs font-medium text-text-subtle">
                Document Type
              </p>
              <p className="text-xs">{poaData.documentType}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle">
                Application Status
              </p>
              <p
                className={`text-xs ${
                  poaData.applicationStatus === "auto_approved"
                    ? "text-text-success"
                    : "text-text-error"
                }`}
              >
                {poaData.applicationStatus.replace(/_/g, " ")}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <h2 className="text-sm font-semibold mb-1">Personal Details</h2>
          <div className="grid grid-cols-2 gap-2 px-2 py-1 bg-bg-base rounded border border-border-subtle">
            <div>
              <p className="text-xs font-medium text-text-subtle">Full Name</p>
              <p className="text-xs">{poaData.fullName}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle">
                Aadhaar Number
              </p>
              <p className="text-xs">{poaData.idNumber}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle">Gender</p>
              <p className="text-xs">
                {poaData.gender === "M"
                  ? "Male"
                  : poaData.gender === "F"
                  ? "Female"
                  : poaData.gender}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle">
                Year of Birth
              </p>
              <p className="text-xs">{poaData.yearOfBirth}</p>
            </div>
            <div className="col-span-2">
              <p className="text-xs font-medium text-text-subtle">
                Father's Name
              </p>
              <p className="text-xs">{poaData.fatherName}</p>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <h2 className="text-sm font-semibold mb-1">Address Details</h2>
          <div className="px-2 py-1 bg-bg-base rounded border border-border-subtle">
            <div className="mb-2">
              <p className="text-xs font-medium text-text-subtle">
                Full Address
              </p>
              <p className="text-xs">{poaData.address}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs font-medium text-text-subtle">
                  Province/State
                </p>
                <p className="text-xs">{poaData.addressProvince}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-text-subtle">Zip Code</p>
                <p className="text-xs">{poaData.addressZipCode}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <h2 className="text-sm font-semibold mb-1">Document Images</h2>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs font-medium text-text-subtle mb-1">
                Front Side
              </p>
              {(kycDumpData.poaFrontUrl || poaData.frontImageUrl) && (
                <div className="border border-border-subtle rounded overflow-hidden">
                  <img
                    src={kycDumpData.poaFrontUrl || poaData.frontImageUrl}
                    alt="Aadhaar Front"
                    className="w-full h-auto"
                  />
                </div>
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-text-subtle mb-1">
                Back Side
              </p>
              {(kycDumpData.poaBackUrl || poaData.backImageUrl) && (
                <div className="border border-border-subtle rounded overflow-hidden">
                  <img
                    src={kycDumpData.poaBackUrl || poaData.backImageUrl}
                    alt="Aadhaar Back"
                    className="w-full h-auto"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const tabs: Array<{ label: string; component: React.ReactNode }> = [];

  if (hasPoiData) {
    tabs.push({
      label: "POI",
      component: renderPoiContent(),
    });
  }

  if (hasPoaData) {
    tabs.push({
      label: "POA",
      component: renderPoaContent(),
    });
  }

  if (tabs.length === 0) {
    tabs.push({
      label: "No Data",
      component: (
        <div className="p-3 text-center text-text-subtle">
          <p>No KYC data available</p>
        </div>
      ),
    });
  }

  return (
    <SlidingPanel
      isOpen={isOpen}
      showConfirm={false}
      heading={
        tabs.length > 1
          ? "KYC Dump"
          : tabs[0]?.label === "POI"
          ? "POI Dump"
          : "POA Dump"
      }
      onClose={() => {
        onClose();
      }}
    >
      {tabs.length > 1 ? <Switcher tabs={tabs} /> : tabs[0]?.component}
    </SlidingPanel>
  );
};

export default KycDumpSlider;
